import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
import DashboardCards from './components/DashboardCards';
import DashboardTable from './components/DashboardTable';
import './ConsGuardDashboard.css';

import { getConsistencyGuardDashboardApi, getAllProjects, Project, getAssignableReviewersApi } from '../../services/AxiosServices';

const formatDateOnly = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}.${month}.${day}`;
};

const formatDateOrTime = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatDateSubtext = (dateStr: string) => {
    if (!dateStr) return 'Today';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatCategory = (cat: string) => {
    if (!cat) return '';
    return cat.replace(/_/g, ' ');
};

const formatStatus = (status: string) => {
    if (!status) return 'Open';
    const s = status.toUpperCase();
    if (s === 'OPEN') return 'Open';
    if (s === 'IN_REVIEW' || s === 'REVIEW') return 'Review';
    if (s === 'APPROVED' || s === 'RESOLVED') return 'Approved';
    if (s === 'ARCHIVED' || s === 'ARCHIVE') return 'Archived';
    return status;
};

const ConsGuardDashboard: React.FC = () => {
    const { getAccessTokenSilently } = useAuth0();
    const location = useLocation();
    const [projects, setProjects] = useState<Project[]>([]);
    const [selectedProjectId, setSelectedProjectId] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const [data, setData] = useState<any[]>([]);
    const [dossier, setDossier] = useState<any>({});
    const [metrics, setMetrics] = useState<any>({});
    const [stats, setStats] = useState<any>({});
    const [commonCategories, setCommonCategories] = useState<any[]>([]);
    const [reviewerMap, setReviewerMap] = useState<Record<string, string>>({});

    // Fetch projects list from API on initial load
    useEffect(() => {
        const fetchProjects = async () => {
            try {
                let token = '';
                try {
                    token = await getAccessTokenSilently();
                } catch (tokenErr) {
                    console.warn('Unable to get access token silently:', tokenErr);
                }

                const projectsData = await getAllProjects<Project[]>(token);
                if (projectsData && Array.isArray(projectsData) && projectsData.length > 0) {
                    setProjects(projectsData);
                    if (!selectedProjectId) {
                        const urlProject = new URLSearchParams(location.search).get('project');
                        const storedParam = sessionStorage.getItem('cg-selected-project');
                        console.log(storedParam);

                        const resolved =
                            (urlProject && projectsData.some((p) => p.project_id === urlProject))
                                ? urlProject
                                : (storedParam && projectsData.some((p) => p.project_id === storedParam))
                                    ? storedParam
                                    : projectsData[0].project_id;

                        setSelectedProjectId(resolved);

                        if (resolved) {
                            sessionStorage.setItem('cg-selected-project', resolved);
                        }
                    }
                }
            } catch (err: any) {
                console.error('Error fetching project list:', err);
                setError(err.message || 'Failed to fetch project list');
            }
        };

        fetchProjects();
    }, [getAccessTokenSilently]);

    useEffect(() => {
        const fetchReviewers = async () => {
            try {
                let token = '';
                try {
                    token = await getAccessTokenSilently();
                } catch (tokenErr) {
                    console.warn('Unable to get access token silently:', tokenErr);
                }

                const reviewers: any[] | null = await getAssignableReviewersApi<any[]>(token);
                if (reviewers && Array.isArray(reviewers)) {
                    const map: Record<string, string> = {};
                    reviewers.forEach((r: any) => {
                        map[r.user_id] = r.nickname;
                    });
                    setReviewerMap(map);
                }
            } catch (err: any) {
                console.error('Error fetching assignable reviewers:', err);
            }
        };

        fetchReviewers();
    }, [getAccessTokenSilently]);

    const fetchDashboardData = useCallback(async (projectId: string) => {
        if (!projectId) return;

        setLoading(true);
        setError(null);
        try {
            let token = '';
            try {
                token = await getAccessTokenSilently();
            } catch (tokenErr) {
                console.warn('Unable to get silently access token:', tokenErr);
            }

            const res: any = await getConsistencyGuardDashboardApi(projectId, token);

            if (res) {
                const summary = res.summary || {};
                const instances = res.conflicted_rules_instances || res.inconsistencies || [];

                // Find matching project to get project display name
                const matchingProject = projects.find((p) => p.project_id === projectId);
                const projectNameToDisplay = matchingProject?.name || summary.project_id || projectId;

                // 1. Dossier data
                setDossier({
                    name: projectNameToDisplay,
                    owner: summary.owner || 'SYSTEM',
                    inconsistenciesCount: summary.total_active ?? instances.length ?? 0,
                    resolvedPercentage: summary.percentage_approved ? Math.round(summary.percentage_approved) : 0
                });

                // 2. Metrics data
                setMetrics({
                    newInconsistencies: {
                        value: summary.new_inconsistencies?.value ?? 0,
                        label: 'New Inconsistencies',
                        subtext: summary.new_inconsistencies?.period ? `Period: ${summary.new_inconsistencies.period}` : 'Detected since last scan'
                    },
                    resolved: {
                        value: summary.resolved?.value ?? 0,
                        label: 'Resolved',
                        subtext: summary.resolved?.period ? `Period: ${summary.resolved.period}` : 'Closed this month'
                    },
                    inReview: {
                        value: summary.in_review ?? 0,
                        label: 'Review',
                        subtext: 'Assigned and active'
                    },
                    highSeverityOpen: {
                        value: summary.open_high_severity ?? 0,
                        label: 'High severity open',
                        subtext: 'Potential audit risk'
                    },
                    needsAssignment: {
                        value: summary.not_assigned ?? 0,
                        label: 'Needs assignment',
                        subtext: 'No reviewer selected'
                    },
                    lastScan: {
                        value: summary.last_scan ? formatDateOrTime(summary.last_scan) : '-',
                        label: 'Last scan',
                        subtext: summary.last_scan ? formatDateSubtext(summary.last_scan) : 'Today'
                    }
                });

                // 3. Stats data
                setStats({
                    averageTimeToResolve: summary.avg_time_to_resolve != null ? `${summary.avg_time_to_resolve}d` : 'N/A',
                    resolutionRate: summary.resolution_rate != null ? `${Math.round(summary.resolution_rate)}%` : '0%'
                });

                // 4. Common Categories
                if (summary.most_common_categories && Array.isArray(summary.most_common_categories)) {
                    setCommonCategories(
                        summary.most_common_categories.map((c: any) => ({
                            category: formatCategory(c.category),
                            count: c.count ?? 0
                        }))
                    );
                }

                // 5. Map table rows: look up reviewer nickname from the assignable reviewers list
                const mappedTableData = instances.map((item: any, index: number) => {
                    let reviewerName = 'SYSTEM';

                    const rawReviewer = typeof item.reviewer === 'object'
                        ? (item.reviewer?.user_name || '')
                        : (item.reviewer || '');

                    if (rawReviewer && reviewerMap[rawReviewer]) {
                        reviewerName = reviewerMap[rawReviewer];
                    } else if (rawReviewer) {
                        reviewerName = rawReviewer.split('|')[1] || rawReviewer;
                    }

                    const scanId = item.scan_id || item.scanId || summary.scan_id || summary.project_id || projectId;
                    const ruleId = item.rule_id || item.ruleId || item.id;
                    const rawIncId = item.inconsistency_id || item.inconsistencyId || item.id;
                    const inconsistencyNumber = item.inconsistency_number || `INC_${index + 1}`;

                    return {
                        inconsistency_number: inconsistencyNumber,
                        real_inconsistency_id: rawIncId,
                        scan_id: scanId,
                        rule_id: ruleId,
                        project_id: projectId,
                        title: item.title,
                        category: formatCategory(item.category),
                        severity: item.severity || 'MEDIUM',
                        reviewer: reviewerName,
                        status: formatStatus(item.activity_status || item.status),
                        detected: formatDateOnly(item.detected_date || item.detected),
                        isNew: false
                    };
                });

                setData(mappedTableData);
            }
        } catch (err: any) {
            console.error('Error fetching consguard dashboard data:', err);
            setError(err.message || 'Failed to fetch consistency guard data');
        } finally {
            setLoading(false);
        }
    }, [getAccessTokenSilently, projects, reviewerMap]);

    useEffect(() => {
        if (selectedProjectId) {
            fetchDashboardData(selectedProjectId);
        }
    }, [selectedProjectId, fetchDashboardData]);

    const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value;
        setSelectedProjectId(value);
        sessionStorage.setItem('cg-selected-project', value);
        const params = new URLSearchParams();
        params.set('project', value);
        window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
    };

    return (
        <div className="cg-dashboard-container">
            <div className="cg-dashboard-header">
                <div className="cg-header-breadcrumb">
                    <h2>Consistency Guard Dashboard</h2>
                </div>
                <div className="cg-dossier-dropdown-wrapper">
                    <select className="cg-dossier-dropdown" value={selectedProjectId} onChange={handleProjectChange}>
                        {projects.map((p) => (
                            <option key={p.project_id} value={p.project_id}>
                                {p.name || p.project_id}
                            </option>
                        ))}
                    </select>
                    <div className="cg-dossier-dropdown-arrow">
                        <svg width="10" height="6" viewBox="0 0 10 6" fill="none"><path d="M1 1L5 5L9 1" stroke="#64748B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </div>
                </div>
            </div>
            <div className="cg-header-divider"></div>

            {loading && <div style={{ padding: '12px 16px', color: '#64748b', fontSize: '14px' }}>Loading dashboard data...</div>}
            {error && <div style={{ padding: '12px 16px', color: '#ef4444', fontSize: '14px' }}>Notice: {error}</div>}

            <DashboardCards
                dossier={dossier}
                metrics={metrics}
                stats={stats}
                commonCategories={commonCategories}
            />

            <DashboardTable
                data={data}
            />
        </div>
    );
};

export default ConsGuardDashboard;

